import * as React from 'react';

import {
  Paper,
  Button,
  Typography,
  Grid,
  Snackbar,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
} from '@material-ui/core';
import MuiAlert from '@material-ui/lab/Alert';
import { useHistory, useLocation } from 'react-router-dom';
import { useForm } from 'react-hook-form';

import { useStyles } from './styles';
import UserData from 'components/TruckQuotation/UserData/UserData';
import SubTitle from 'components/TruckQuotation/SubTitle/SubTitle';
import QuoteType from 'components/TruckQuotation/QuoteType/QuoteType';
import VehicleInfo from 'components/TruckQuotation/VehicleInfo/VehicleInfo';
import {
  civilStatus,
  quoteTypeData,
  documentType,
  stratum,
  educationLevel as educationLevelItems,
  occupation,
  profession,
  services,
  useForVehicleParticular,
  useForVehiclePublic,
  defaultValues,
} from './resources';
import { useInsurersTruck } from 'hooks/useInsurersTruck';
import { TEventTarget } from 'types';
import { formatDate } from 'utils/utils';
import {
  createQuotation,
  getFasecoldaCode,
  getValidateQuotation,
  getRefFasecolda,
} from 'services/QuotationServiceTruck';
import { cities } from './cities';
import matchSorter from 'match-sorter';
import { ISelect } from 'types/CarsQuotation/IDataInsure';
import { LICENSE_PLATE_VALIDATION } from 'constants/index';
import swal from 'sweetalert';
import { BootstrapInput } from 'components/Global/PickerSelect/PickerSelect';

const CarsQuotation: React.FC = () => {
  const classes = useStyles();

  const location = useLocation() as ILocationCarsQuotation;

  const { defaultValues: userDefaultValues } = useInsurersTruck();

  const { handleSubmit, control, errors, watch, setValue, reset } = useForm<ICarsQuotation>({
    defaultValues: userDefaultValues || defaultValues,
  });
  const watchAllFields = watch(['tVehicle', 'service', 'checkAccesories']);

  React.useEffect(() => {
    if (userDefaultValues && !location.state?.quotation) {
      reset(userDefaultValues);
    }
  }, [userDefaultValues, reset, location.state]);

  const [errorMessage, setErrorMessage] = React.useState<string>('');
  const [openSnack, setOpenSnack] = React.useState<boolean>(false);

  const [refsFasecolda, setRefsFasecolda] = React.useState<any>({});

  React.useEffect(() => {
    const currentTVehicle = watchAllFields.tVehicle;
    const codeFasecolda = watch('codeFasecolda');
    const modelo = watch('modelo');

    if (currentTVehicle === 'usado' && codeFasecolda && modelo) {
      if (Object.keys(refsFasecolda).length > 0) {
        const fModelo = `f${modelo}`;
        const valueInsure = refsFasecolda[fModelo];
        if (valueInsure) {
          setValue('valueInsured', valueInsure);
        }
      } else {
        // Si no tenemos los datos, cargarlos primero
        setFasecoldaRef(codeFasecolda).then((response) => {
          if (response && modelo) {
            const fModelo = `f${modelo}`;
            const valueInsure = response[fModelo];
            if (valueInsure) {
              setValue('valueInsured', valueInsure);
            }
          }
        });
      }
    }
  }, [watchAllFields.tVehicle, watch, setValue, refsFasecolda]);
  const [disabledFasecoldaField, setDisabledFasecoldaField] = React.useState<boolean>(false);
  const [loading, setLoading] = React.useState<boolean>(false);
  const [typeQuote, setTypeQuote] = React.useState('Auto_P');

  const handleChangeTypeQuote = (event: any) => {
    setTypeQuote(event.target.value);
  };

  const onError = (error: string) => {
    setOpenSnack(true);
    setErrorMessage(error);
  };

  const history = useHistory();

  const filterCities = (inputValue: string) => {
    const filterCitiesBySearch =
      cities !== null
        ? matchSorter(cities, inputValue, {
            keys: ['value', 'label'],
          })
        : [];
    return filterCitiesBySearch;
  };

  const loadCitiesOptions = (inputValue: string, callback: any) => {
    setTimeout(() => {
      callback(filterCities(inputValue));
    }, 500);
  };

  const handleInputCitiesChange = (newValue: string) => {
    const inputValue = newValue;
    return inputValue;
  };

  const onCloseSnackBar = () => {
    setOpenSnack(false);
  };

  const onValidateLicensePlate = (event: TEventTarget, props: any) => {
    const validationRegex = LICENSE_PLATE_VALIDATION;
    const plate = event.target.value;

    if (validationRegex.test(plate)) {
      setLoading(true);
      getFasecoldaCode(plate, onError)
        .then(({ fasecolda, modelo }) => {
          if (watchAllFields.tVehicle === 'usado' && fasecolda && modelo) {
            const fModelo = `f${modelo}`;

            setValue('codeFasecolda', fasecolda);
            setValue('modelo', modelo);

            setFasecoldaRef(fasecolda).then((response) =>
              setValue('valueInsured', response[fModelo])
            );
            setDisabledFasecoldaField(true);
            setLoading(false);
          } else {
            swal(
              '¡Alto!',
              'Esta placa está matricula en el runt o esta siendo usada en otro vehiculo.',
              'warning'
            );
            setLoading(false);
          }
        })
        .catch(() => setLoading(false));
      return props.onBlur(plate);
    } else {
      onError('Ups, La placa ingresada no es valida.');
      setDisabledFasecoldaField(false);
      return props.onBlur(plate);
    }
  };

  const onCreateQuotation = (bodyObject: any) => {
    createQuotation(bodyObject, onError).then(({ uuid }) => {
      history.push({
        pathname: `/app/cotizador/pesados/resumen/${uuid}`,
      });
      return uuid;
    });
  };

  const setFasecoldaRef = async (fasecoldaCode: string | undefined) => {
    if (fasecoldaCode !== undefined) {
      setLoading(true);
      try {
        const refs = await getRefFasecolda(fasecoldaCode, onError);
        setRefsFasecolda(refs.guiaFasecolda.data);
        setValue('brand', refs.guiaFasecolda.data.fMarca);
        setValue('ref1', refs.guiaFasecolda.data.fReferencia1);
        setValue('ref2', refs.guiaFasecolda.data.fReferencia2);
        setValue('ref3', refs.guiaFasecolda.data.fReferencia3);

        setLoading(false);
        return refs.guiaFasecolda.data;
      } catch (err) {
        setLoading(false);
      }
    }
  };

  const onChangeFasecolda = (event: TEventTarget, props: any) => {
    if (event.target.value !== '') {
      setDisabledFasecoldaField(false);
      setFasecoldaRef(event.target.value);
      setValueUseVehicle(event.target.value);
      return props.onBlur(event.target.value);
    }
    setValueUseVehicle(event.target.value);
    return props.onBlur(event.target.value);
  };

  const setValueUseVehicle = (fasecolda: string) => {
    const valuesTaxiAndHerramienta = ['01', '02', '06', '07', '08', '20', '21'];
    const valuesPesadosRemolque = ['23', '25'];
    const valuesPesadosCarga = ['04', '10', '11', '12', '13', '14', '22'];
    const typeVehicle = fasecolda?.substring(3, 5);
    if (valuesTaxiAndHerramienta.includes(typeVehicle)) {
      setValue('useVehicle', 'herramientaDeTrabajo');
    } else if (valuesPesadosRemolque.includes(typeVehicle)) {
      setValue('useVehicle', 'remolque');
    } else if (valuesPesadosCarga.includes(typeVehicle)) {
      setValue('useVehicle', 'cargaNacional');
    }
  };

  const onModeloChange = (event: TEventTarget, props: any) => {
    if (event.target.value !== '') {
      setLoading(true);
      const fModelo = `f${event.target.value}`;
      const valueInsure = refsFasecolda[fModelo];
      setValue('valueInsured', valueInsure);
      setLoading(false);
      return props.onBlur(event.target.value);
    } else {
      setLoading(false);
      return props.onBlur(event.target.value);
    }
  };

  const onSubmitQuotationForm = (formDataState: ICarsQuotation) => {
    const carModelo = formDataState.modelo;
    const valueInsuredAsNumber = parseInt(formDataState.valueInsured, 10);

    if (valueInsuredAsNumber !== 0) {
      const newSelectedInsurers: string[] = [];

      formDataState.selectedInsurers.map((insure: ISelect) =>
        newSelectedInsurers.push(insure.value)
      );

      const bodyObject = {
        ...formDataState,
        quoteType: typeQuote,
        selectedInsurers: newSelectedInsurers,
        cityCirculation:
          formDataState.optionalCity.value === undefined
            ? [formDataState.cityCirculation.value]
            : [formDataState.cityCirculation.value, formDataState.optionalCity.value],
        nDocument: parseInt(formDataState.nDocument, 10),
        stratum: parseInt(formDataState.stratum, 10),
        modelo: carModelo !== undefined && parseInt(carModelo, 10),
        valueInsured: valueInsuredAsNumber,
        valueAccesories: formDataState.valueAccesories
          ? parseInt(formDataState.valueAccesories, 10)
          : 0,
        fBorn: formDataState.fBorn,
        nChildren: parseInt(formDataState.nChildren as any, 10) || 0,
      };

      getValidateQuotation(formDataState.licenseVehicle, onError)
        .then((response) => {
          setLoading(true);
          if (response.message) {
            onCreateQuotation(bodyObject);
            setLoading(false);
          } else {
            swal({
              title: 'Esta cotización ya se encuentra realizada.',
              text: `Fecha de cotización: ${formatDate(response.createdAt)}. Placa: ${
                response.licenseVehicle
              }. Modelo: ${response.modelo}, Por: ${response.createdBy.name}`,
              icon: 'warning',
              buttons: {
                cancel: 'No cotizar',
                catch: {
                  text: 'Quiero cotizar',
                  value: 'catch',
                },
              },
            } as any).then((value) => {
              switch (value) {
                case 'catch':
                  return onCreateQuotation(bodyObject);
                default:
                  return;
              }
            });
            setLoading(false);
          }
        })
        .catch(() => setLoading(false));
    } else {
      swal(
        `Lo sentimos`,
        `El valor asegurado no puede ser 0, revisa el formulario si los datos ingresados son corrector e intenta de nuevo.`,
        'error'
      );
    }
  };

  React.useEffect(() => {
    document.title = 'Cotizador de autos';
    if (location.state !== undefined) {
      const quoteByLocation = location.state.quotation;
      const quotationTransform = Object.entries(quoteByLocation);
      quotationTransform.map((quote) => setValue(quote[0], quote[1]));
    }
  }, [location, setValue]);
  return (
    <React.Fragment>
      <form onSubmit={handleSubmit(onSubmitQuotationForm)}>
        <Paper className={classes.paper}>
          <Typography component="h1" variant="h4" align="center" className={classes.title}>
            Cotizador - Seguro Todo Riesgo Autos
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <SubTitle title="Tipo de Cotización" />
            </Grid>
            <QuoteType
              control={control}
              quoteType={quoteTypeData}
              disabledInsures={false}
              disabledQuoteType
              setValue={setValue}
            />
            <Grid item xs={12} sm={4}>
              <FormControl className={classes.margin} disabled>
                <Typography className={classes.label}>Ramo a Cotizar</Typography>
                <Select
                  labelId="demo-customized-select-label"
                  id="demo-customized-select"
                  value={typeQuote}
                  onChange={handleChangeTypeQuote}
                  input={<BootstrapInput />}>
                  <MenuItem value={'Auto_P'}>Auto_P</MenuItem>
                  <MenuItem value={'moto'}>Moto</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <SubTitle title="Datos Personales" />
            </Grid>
            <UserData
              control={control}
              civilStatusItems={civilStatus}
              documentTypeItems={documentType}
              stratumItems={stratum}
              educationLevelItems={educationLevelItems}
              occupationItems={occupation}
              professionItems={profession}
              errors={errors}
            />
            <Grid item xs={12}>
              <SubTitle title="Información del Vehículo" />
            </Grid>
            <VehicleInfo
              control={control}
              serviceItems={services}
              useVehicleItems={
                watchAllFields.service === 'particular'
                  ? useForVehicleParticular
                  : useForVehiclePublic
              }
              loadCitiesOptions={loadCitiesOptions}
              handleInputCitiesChange={handleInputCitiesChange}
              errors={errors}
              onValidateLicensePlate={onValidateLicensePlate}
              checkboxAccesories={watchAllFields.checkAccesories}
              onChangeFasecolda={onChangeFasecolda}
              disabledFasecoldaField={disabledFasecoldaField}
              onModeloChange={onModeloChange}
              disableSecuredValue={watchAllFields.tVehicle === 'nuevo' ? false : true}
            />
            <Grid item xs={12}>
              <div className={classes.buttons}>
                {loading ? (
                  <CircularProgress color="primary" thickness={4} />
                ) : (
                  <Button
                    variant="contained"
                    color="primary"
                    className={classes.button}
                    type="submit"
                    disableElevation>
                    Cotizar
                  </Button>
                )}
              </div>
              {Object.keys(errors).length > 0 && (
                <Typography
                  variant="caption"
                  display="block"
                  gutterBottom
                  align="center"
                  style={{ color: 'red' }}>
                  Hay campos incompletos o con errores, por favor revisarlos.
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>
        <Snackbar open={openSnack} autoHideDuration={6000} onClose={onCloseSnackBar}>
          <MuiAlert elevation={6} variant="filled" onClose={onCloseSnackBar} severity="error">
            {errorMessage}
          </MuiAlert>
        </Snackbar>
      </form>
    </React.Fragment>
  );
};

export default CarsQuotation;
